import { _ } from '../../../lib';
import { addNewNoteForm } from './add';
import { archiveNotes, deleteNotes } from './delete';
import { downloadNotes } from './download';
import { editNote, editNoteTags } from './edit';
import { viewNoteHistory } from './history';
import { getFlattenedTags } from './tags';
export const getMetaActions = (rs) => {
    const allNotes = _.chain(rs.props.notes)
        .filter(x => !x.Deleted || rs.state.showDeleted)
        .filter(x => !x.Archived || rs.state.showArchived)
        .value();
    const noteCountByTag = _.chain(allNotes)
        .flatMap(x => x.Tags)
        .countBy(x => x)
        .value();
    const tags = _.chain(getFlattenedTags(rs))
        .sortBy(x => x.DisplayName)
        .map(x => ({
        Tag: x,
        Count: noteCountByTag[x.ID] ?? 0,
    }))
        .filter(x => x.Count > 0)
        .value();
    return _.compact([
        rs.props.adding
            ? {
                label: 'Add',
                icon: '/static/img/i8/w11-add.svg',
                onClick: () => {
                    addNewNoteForm(rs);
                },
            }
            : null,
        rs.props.onRefreshClick
            ? {
                label: 'Refresh',
                icon: '/static/img/i8/ios16-refresh.svg',
                onClick: () => {
                    rs.updateState({ loadingCoverMessage: 'Refreshing...' });
                    rs.props.onRefreshClick?.(() => {
                        rs.updateState({ loadingCoverMessage: null });
                    });
                },
            }
            : null,
        rs.props.adding || rs.props.onRefreshClick ? '---' : null,
        {
            label: 'Show Archived',
            icon: () => rs.state.showArchived
                ? '/static/img/i8/material-outline-checkmark.svg'
                : undefined,
            dontClose: true,
            onClick: () => {
                rs.updateState({
                    showArchived: !rs.state.showArchived,
                });
            },
        },
        {
            label: 'Show Deleted',
            icon: () => rs.state.showDeleted
                ? '/static/img/i8/material-outline-checkmark.svg'
                : undefined,
            dontClose: true,
            onClick: () => {
                rs.updateState({
                    showDeleted: !rs.state.showDeleted,
                });
            },
        },
        rs.props.tagging
            ? {
                label: 'Show Tag Indicators',
                labelDesc: 'Whether the coloured circle tag indicators should be shown in the note list',
                icon: () => rs.state.showTagIndicators
                    ? '/static/img/i8/material-outline-checkmark.svg'
                    : undefined,
                dontClose: true,
                onClick: () => {
                    rs.updateState({
                        showTagIndicators: !rs.state.showTagIndicators,
                    });
                },
            }
            : null,
        {
            label: 'Show Compact View',
            labelDesc: 'Shows the list of notes in a more compact view',
            icon: () => rs.state.showCompactView
                ? '/static/img/i8/material-outline-checkmark.svg'
                : undefined,
            dontClose: true,
            onClick: () => {
                rs.updateState({
                    showCompactView: !rs.state.showCompactView,
                });
            },
        },
        rs.props.tagging
            ? {
                label: 'Filter by tag',
                items: [
                    {
                        label: `Show all tags (${allNotes.length})`,
                        dontClose: true,
                        icon: () => !rs.state.filteredTags || !rs.state.filteredTags.length
                            ? '/static/img/i8/material-outline-checkmark.svg'
                            : undefined,
                        onClick: () => {
                            rs.updateState({
                                filteredTags: null,
                            });
                        },
                    },
                    ..._.map(tags, x => ({
                        label: `${x.Tag.DisplayName} (${x.Count})`,
                        dontClose: true,
                        icon: () => rs.state.filteredTags?.includes(x.Tag.ID)
                            ? '/static/img/i8/material-outline-checkmark.svg'
                            : undefined,
                        onClick: () => {
                            rs.updateState(s => {
                                const filteredTags = s.filteredTags?.includes(x.Tag.ID)
                                    ? s.filteredTags.filter(t => t !== x.Tag.ID)
                                    : (s.filteredTags ?? []).concat(x.Tag.ID);
                                return {
                                    filteredTags: filteredTags.length == 0
                                        ? null
                                        : filteredTags,
                                };
                            });
                        },
                    })),
                ],
            }
            : null,
        '---',
        {
            label: 'Download',
            icon: '/static/img/i8/color-download-from-the-cloud.svg',
            onClick: () => {
                downloadNotes(rs);
            },
        },
        rs.props.editing
            ? {
                label: 'Generate',
                icon: '/static/img/i8/color-sparkles.svg',
                labelDesc: 'Generate title/body for all notes that lack them',
                onClick: () => {
                },
            }
            : null,
    ]);
};
export const getNoteActions = (rs, selectedNotes) => {
    const note = selectedNotes.length == 1 ? selectedNotes[0] : null;
    const hasTitle = Boolean(note?.Title ?? false);
    const hasNote = Boolean(note?.Note ?? false);
    const canTranslate = hasTitle || hasNote;
    const generationInfo = getGenerativeActionType({ rs, note });
    const additionalActions = rs.props.additionalActions?.(selectedNotes) ?? [];
    const divider = '---';
    return [
        rs.props.editing
            ? {
                label: 'Edit note',
                labelDesc: 'Edit resident note',
                icon: '/static/img/i8/color-edit.svg',
                onClick: () => {
                    editNote({ rs, note });
                },
            }
            : null,
        !rs.props.editing && rs.props.tagging?.onUpdate
            ? {
                label: 'Edit tags',
                labelDesc: 'Edit the tags for the note',
                icon: '/static/img/i8/color-edit.svg',
                onClick: () => {
                    editNoteTags({ rs, note });
                },
            }
            : null,
        {
            label: 'Copy text',
            labelDesc: 'Copies the title and note to the clipboard',
            icon: '/static/img/i8/color-copy.svg',
            onClick: () => {
                const title = note.Title ?? '';
                const body = note.Note ?? '';
                const text = `${title}\n\n${body}`.trim();
                navigator.clipboard.writeText(text).catch(console.error);
            },
        },
        rs.props.editing ? divider : null,
        rs.props.editing
            ? {
                label: generationInfo?.label ?? '',
                labelDesc: generationInfo?.labelDesc,
                icon: '/static/img/i8/color-sparkles.svg',
                hidden: !generationInfo,
                onClick: generationInfo?.action,
            }
            : null,
        rs.props.editing
            ? {
                label: 'Translate',
                labelDesc: 'Translate the note to the selected language',
                icon: '/static/img/i8/color-translate.svg',
                hidden: !canTranslate,
                onClick: () => {
                    editNote({
                        rs,
                        note,
                        options: {
                            translate: true,
                        },
                    });
                },
            }
            : null,
        additionalActions.length > 0 ? divider : null,
        ...additionalActions,
        rs.props.deleting || rs.props.history ? divider : null,
        rs.props.history
            ? {
                label: 'View history',
                labelDesc: 'View the previous edits of this note',
                icon: '/static/img/i8/w11c-history.svg',
                onClick: () => {
                    viewNoteHistory(note);
                },
            }
            : null,
        rs.props.archiving
            ? {
                label: 'Archive note',
                icon: '/static/img/i8/color-archive.svg',
                onClick: () => {
                    archiveNotes(rs, [note.ID]);
                },
            }
            : null,
        rs.props.deleting
            ? {
                label: 'Delete note',
                labelDesc: 'Delete resident note',
                icon: '/static/img/i8/color-trash-delete.svg',
                onClick: () => {
                    deleteNotes(rs, [note.ID]);
                },
            }
            : null,
    ];
};
const getGenerativeActionType = (input) => {
    const { rs, note } = input;
    if (!note) {
        return null;
    }
    const hasAttachment = Boolean(note.Attachment?.Checksum);
    const hasTitle = Boolean(note.Title);
    const hasNote = Boolean(note.Note);
    const canGenerate = !(rs.props.disableGenerativeAI ?? false);
    const canGenerateTitle = !hasTitle && (hasAttachment || hasNote) && canGenerate;
    const canGenerateSummary = !hasNote && (hasAttachment || hasTitle) && canGenerate;
    if (canGenerateTitle && canGenerateSummary) {
        return {
            label: 'Generate Summary',
            labelDesc: 'Generate a title and summary for the note based on the attachment',
            action: () => {
                editNote({
                    rs,
                    note,
                    options: {
                        generateTitle: true,
                        generateSummary: true,
                    },
                });
            },
        };
    }
    if (canGenerateTitle) {
        return {
            label: 'Generate Title',
            labelDesc: 'Generate a title for the note based on the attachment and body text',
            action: () => {
                editNote({
                    rs,
                    note,
                    options: {
                        generateTitle: true,
                    },
                });
            },
        };
    }
    if (canGenerateSummary) {
        return {
            label: 'Generate Summary',
            labelDesc: 'Generate a summary based on the attachment and title',
            action: () => {
                editNote({
                    rs,
                    note,
                    options: {
                        generateSummary: true,
                    },
                });
            },
        };
    }
    return null;
};
//# sourceMappingURL=actions.js.map